import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Key, ArrowLeft } from 'lucide-react';

interface MFABackupCodeVerificationProps {
  onVerify: (code: string) => Promise<void>;
  onCancel: () => void;
  onBackToMFA: () => void;
}

export function MFABackupCodeVerification({ 
  onVerify, 
  onCancel,
  onBackToMFA 
}: MFABackupCodeVerificationProps) {
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('Please enter a backup code');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      await onVerify(code.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid backup code');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-4">
            <Key className="h-8 w-8 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl font-bold">Use Backup Code</CardTitle>
          <CardDescription>
            Enter one of your backup codes to access your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="backup-code">Backup Code</Label>
              <Input
                id="backup-code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX-XXXX"
                className="text-center font-mono tracking-wider"
                autoComplete="off"
                autoFocus
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Alert>
              <AlertDescription>
                Each backup code can only be used once. After using this code, you'll have one less backup code available.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Button
                type="submit"
                disabled={isVerifying || !code.trim()}
                className="w-full"
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Code'
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={onBackToMFA}
                disabled={isVerifying}
                className="w-full"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Authenticator
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={onCancel}
                disabled={isVerifying}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
